import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { milestoneProgress, PIPELINE_STATUSES } from '../../lib/art-pipeline.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * The art pipeline's milestones: each one's progress, and adding, editing and
 * deleting them.
 *
 * Fires `milestones-changed` (detail: the milestones) and `close`.
 */
class PipelineMilestonesPanel extends BaseComponent {
    static properties = {
        milestones: { type: Array, attribute: false },
        items: { type: Array, attribute: false },
        _editingId: { type: String, state: true },
        _form: { type: Object, state: true },
        _confirmDeleteId: { type: String, state: true },
        _busy: { type: Boolean, state: true },
        _error: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9998; }
        .modal-panel { background: white; border-radius: 8px; width: 90vw; max-width: 720px; max-height: 85vh; display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2); }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1rem 1.25rem; overflow-y: auto; flex: 1; font-size: 0.9rem; }
        .milestone { border: 1px solid #e5e7eb; border-radius: 6px; padding: 0.75rem; margin-bottom: 0.75rem; }
        .milestone-head { display: flex; justify-content: space-between; gap: 1rem; align-items: start; }
        .milestone-name { font-weight: 600; }
        .milestone-meta { color: #666; font-size: 0.8rem; }
        .bar { display: flex; height: 0.5rem; border-radius: 4px; overflow: hidden; background: #e5e7eb; margin: 0.5rem 0 0.25rem; }
        .seg { height: 100%; }
        .seg-draft { background: #d1d5db; }
        .seg-commissioned { background: #22d3ee; }
        .seg-assigned { background: #f59e0b; }
        .seg-done { background: #16a34a; }
        .seg-archived { background: #6b7280; }
        .counts { display: flex; flex-wrap: wrap; gap: 0.75rem; font-size: 0.75rem; color: #555; }
        .dot { display: inline-block; width: 0.55rem; height: 0.55rem; border-radius: 50%; margin-right: 0.25rem; }
        .form-grid { display: grid; grid-template-columns: 1fr 10rem; gap: 0.5rem; }
    `;

    constructor() {
        super();
        this.milestones = [];
        this.items = [];
        this._editingId = null;
        this._form = { name: '', target_date: '', description: '' };
        this._confirmDeleteId = null;
        this._busy = false;
        this._error = null;
    }

    _close() {
        this.dispatchEvent(new CustomEvent('close'));
    }

    async _refresh() {
        const data = await api.get('/api/art-milestones');
        this.dispatchEvent(
            new CustomEvent('milestones-changed', { detail: data.milestones || [] }),
        );
    }

    async _run(fn) {
        this._busy = true;
        this._error = null;
        try {
            await fn();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._busy = false;
        }
    }

    _edit(m) {
        this._editingId = m ? m.id : 'new';
        this._form = {
            name: m?.name || '',
            target_date: m?.target_date || '',
            description: m?.description || '',
        };
    }

    _save(e) {
        e.preventDefault();
        const body = {
            name: this._form.name,
            target_date: this._form.target_date || null,
            description: this._form.description || null,
        };
        return this._run(async () => {
            if (this._editingId === 'new') await api.post('/api/art-milestones', body);
            else await api.put(`/api/art-milestones/${this._editingId}`, body);
            this._editingId = null;
            await this._refresh();
        });
    }

    _delete(id) {
        return this._run(async () => {
            await api.delete(`/api/art-milestones/${id}`);
            this._confirmDeleteId = null;
            await this._refresh();
        });
    }

    render() {
        return html`
            <div class="modal-overlay" @click="${(e) => e.target === e.currentTarget && this._close()}">
                <div class="modal-panel" role="dialog" aria-label="Milestones" @keydown="${(e) => e.key === 'Escape' && this._close()}">
                    <div class="modal-header">
                        <h5>Milestones</h5>
                        <button class="btn-close" aria-label="Close" @click="${this._close}"></button>
                    </div>
                    <div class="modal-body">
                        ${this._error ? html`<div class="alert alert-danger py-2 small">${this._error}</div>` : nothing}
                        ${this.milestones.length === 0 && this._editingId !== 'new' ? html`<p class="text-muted">No milestones yet.</p>` : nothing}
                        ${this.milestones.map((m) => (this._editingId === m.id ? this._renderForm() : this._renderMilestone(m)))}
                        ${
                            this._editingId === 'new'
                                ? this._renderForm()
                                : html`<button class="btn btn-sm btn-outline-primary" @click="${() => this._edit(null)}"><i class="bi bi-plus-lg"></i> New milestone</button>`
                        }
                    </div>
                </div>
            </div>
        `;
    }

    _renderMilestone(m) {
        const progress = milestoneProgress(this.items.filter((i) => i.milestone_id === m.id));
        return html`
            <div class="milestone">
                <div class="milestone-head">
                    <div>
                        <div class="milestone-name">${m.name}</div>
                        <div class="milestone-meta">
                            ${m.target_date ? html`<i class="bi bi-calendar-event"></i> ${m.target_date} · ` : nothing}
                            ${progress.total} item${progress.total === 1 ? '' : 's'} · ${progress.percentDone}% done
                        </div>
                        ${m.description ? html`<div class="small mt-1">${m.description}</div>` : nothing}
                    </div>
                    <div class="d-flex gap-1 flex-shrink-0">
                        ${
                            this._confirmDeleteId === m.id
                                ? html`<span class="small align-self-center">Delete? Its specs stay.</span>
                                <button class="btn btn-sm btn-danger" ?disabled="${this._busy}" @click="${() => this._delete(m.id)}">Delete</button>
                                <button class="btn btn-sm btn-link" @click="${() => (this._confirmDeleteId = null)}">Keep</button>`
                                : html`<button class="btn btn-sm btn-outline-secondary" @click="${() => this._edit(m)}">Edit</button>
                                <button class="btn btn-sm btn-outline-danger" @click="${() => (this._confirmDeleteId = m.id)}" aria-label="Delete ${m.name}"><i class="bi bi-trash"></i></button>`
                        }
                    </div>
                </div>
                ${
                    progress.total
                        ? html`<div class="bar">
                        ${PIPELINE_STATUSES.map((s) =>
                            progress.counts[s.value]
                                ? html`<span class="seg seg-${s.value}" style="width: ${(progress.counts[s.value] / progress.total) * 100}%"></span>`
                                : nothing,
                        )}
                    </div>
                    <div class="counts">
                        ${PIPELINE_STATUSES.map((s) => html`<span><span class="dot seg-${s.value}"></span>${s.label}: ${progress.counts[s.value]}</span>`)}
                    </div>`
                        : nothing
                }
            </div>
        `;
    }

    _renderForm() {
        const set = (key) => (e) => (this._form = { ...this._form, [key]: e.target.value });
        return html`
            <form class="milestone" @submit="${this._save}">
                <div class="form-grid mb-2">
                    <input class="form-control form-control-sm" placeholder="Name" required .value="${this._form.name}" @input="${set('name')}">
                    <input class="form-control form-control-sm" type="date" .value="${this._form.target_date}" @input="${set('target_date')}" aria-label="Target date">
                </div>
                <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Description (optional)" .value="${this._form.description}" @input="${set('description')}"></textarea>
                <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-primary" type="submit" ?disabled="${this._busy || !this._form.name.trim()}">Save</button>
                    <button class="btn btn-sm btn-link" type="button" @click="${() => (this._editingId = null)}">Cancel</button>
                </div>
            </form>
        `;
    }
}

customElements.define('pipeline-milestones-panel', PipelineMilestonesPanel);
